import { useState } from "react";
import { useFieldArray } from "react-hook-form";
import { toast } from "react-hot-toast";
import dayjs from "dayjs";
import { Plus, Trash2 } from "lucide-react";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";
import { DatePickerField } from "components/ui/DatePickerField";
import { Dialog } from "components/ui/Dialog";

import style from "../ReferralForm.module.css";
import { RadiologyResultDetails } from "./ResultDetails/RadiologyResultDetails";
import { ResultDetails } from "./ResultDetails/ResultDetails";

const EMPTY_ENTRY = {
  title: "",
  hospital: "",
  date: "",
  result: "",
  unit: "",
};

const isEntryEmpty = (entry) =>
  !entry.title.trim() &&
  !entry.hospital.trim() &&
  !entry.date &&
  !entry.result.trim() &&
  !entry.unit.trim();

export const InvestigationSection = ({
  form,
  name,
  laboratoryResults,
  radiologyResults,
  withHospital,
  withUnit,
}) => {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newEntries, setNewEntries] = useState([EMPTY_ENTRY]);
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name,
  });

  const isLaboratory = name === "laboratoryInvestigation";
  const results = isLaboratory ? laboratoryResults : radiologyResults;
  const sectionLabel = isLaboratory
    ? "Laboratory Investigations"
    : "Radiology Investigations";
  const investigationLabel = isLaboratory
    ? "Laboratory Investigation"
    : "Radiology Investigation";

  const addItemsToForm = (items) => {
    setIsDetailsModalOpen(false);
    append(items);
  };

  const updateNewEntry = (index, key, value) => {
    setNewEntries((prev) =>
      prev.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [key]: value } : entry
      )
    );
  };

  const addNewEntryRow = () => {
    setNewEntries((prev) => [...prev, EMPTY_ENTRY]);
  };

  const removeNewEntryRow = (index) => {
    setNewEntries((prev) =>
      prev.length > 1
        ? prev.filter((_, entryIndex) => entryIndex !== index)
        : prev
    );
  };

  const closeAddModal = () => {
    setNewEntries([EMPTY_ENTRY]);
    setIsAddModalOpen(false);
  };

  const filledEntries = newEntries.filter((entry) => !isEntryEmpty(entry));

  const handleAddEntries = () => {
    if (!filledEntries.length) {
      toast.error(`Enter at least one ${investigationLabel.toLowerCase()}`);
      return;
    }

    const missingTitle = filledEntries.some((entry) => !entry.title.trim());
    if (missingTitle) {
      toast.error(`${investigationLabel} name is required for every row`);
      return;
    }

    append(
      filledEntries.map((entry) => ({
        title: entry.title.trim(),
        date: entry.date
          ? dayjs(entry.date, "YYYY-MM-DD").format("DD-MM-YYYY")
          : "",
        result: entry.result,
        ...(withHospital ? { hospital: entry.hospital } : {}),
        ...(withUnit ? { unit: entry.unit } : {}),
      }))
    );
    closeAddModal();
  };

  const columns = [
    {
      id: "title",
      header: "Investigation",
      width: "minmax(120px, 1fr)",
      render: (row) => row.title || "-",
    },
    ...(withHospital
      ? [
          {
            id: "hospital",
            header: "Hospital",
            width: "minmax(100px, 1fr)",
            render: (row) => row.hospital || "-",
          },
        ]
      : []),
    {
      id: "date",
      header: "Date",
      width: "110px",
      render: (row) => row.date || "-",
    },
    ...(withUnit
      ? [
          {
            id: "unit",
            header: "Unit",
            width: "minmax(70px, 1fr)",
            render: (row) => row.unit || "-",
          },
        ]
      : []),
    {
      id: "result",
      header: "Result",
      width: "minmax(220px, 3fr)",
      render: (row) => (
        <span className={style.investigationResultCell}>
          {row.result || "-"}
        </span>
      ),
    },
  ];

  const rowActions = [
    {
      label: "Remove",
      icon: Trash2,
      primary: true,
      onClick: (row) => {
        const index = fields.findIndex((field) => field.id === row.id);
        if (index >= 0) remove(index);
      },
    },
  ];

  return (
    <>
      <div className={style.sectionHeader}>
        <span className={style.addReferralFormLabel}>{sectionLabel}</span>
        <div className={style.sectionHeaderActions}>
          <button
            type="button"
            className={style.importButton}
            onClick={() => setIsDetailsModalOpen(true)}
            data-testid={`${name}-import-button`}
          >
            {isLaboratory
              ? "Import Laboratory Results"
              : "Import Radiology Results"}
          </button>
          <Button
            size="small"
            endIcon={Plus}
            onClick={() => setIsAddModalOpen(true)}
            data-testid={`${name}-add-button`}
          >
            {`Add ${investigationLabel}`}
          </Button>
        </div>
      </div>
      <Dialog
        title={
          isLaboratory
            ? "Import Laboratory Results"
            : "Import Radiology Results"
        }
        open={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        width={800}
      >
        {isLaboratory ? (
          <ResultDetails
            items={results || []}
            addItemsToForm={addItemsToForm}
          />
        ) : (
          <RadiologyResultDetails
            items={results || []}
            addItemsToForm={addItemsToForm}
          />
        )}
      </Dialog>
      <Dialog
        title={`Add ${sectionLabel}`}
        open={isAddModalOpen}
        onClose={closeAddModal}
        width={1100}
      >
        <div
          className={`${style.addReferralForm} ${style.addInvestigationForm}`}
        >
          {newEntries.map((entry, index) => (
            <div
              key={index}
              className={`${style.addInvestigationEntryGrid} ${
                withHospital ? style.withHospital : ""
              }`}
            >
              <FormField>
                <label className={style.addReferralFormLabel}>
                  {investigationLabel}
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={entry.title}
                  onChange={(e) =>
                    updateNewEntry(index, "title", e.target.value)
                  }
                  data-testid={`${name}-new-title-${index}`}
                />
              </FormField>
              {!!withHospital && (
                <FormField>
                  <label className={style.addReferralFormLabel}>Hospital</label>
                  <input
                    type="text"
                    className="form-control"
                    value={entry.hospital}
                    onChange={(e) =>
                      updateNewEntry(index, "hospital", e.target.value)
                    }
                  />
                </FormField>
              )}
              <FormField>
                <label className={style.addReferralFormLabel}>Date</label>
                <div className={style.datePickerCell}>
                  <DatePickerField
                    value={entry.date}
                    onChange={(value) => updateNewEntry(index, "date", value)}
                    placeholder="DD-MM-YYYY"
                  />
                </div>
              </FormField>
              <FormField>
                <label className={style.addReferralFormLabel}>Result</label>
                <input
                  type="text"
                  className="form-control"
                  value={entry.result}
                  onChange={(e) =>
                    updateNewEntry(index, "result", e.target.value)
                  }
                />
              </FormField>
              {!!withUnit && (
                <FormField>
                  <label className={style.addReferralFormLabel}>Unit</label>
                  <input
                    type="text"
                    className="form-control"
                    value={entry.unit}
                    onChange={(e) =>
                      updateNewEntry(index, "unit", e.target.value)
                    }
                  />
                </FormField>
              )}
              <button
                type="button"
                className={style.entryRemoveButton}
                onClick={() => removeNewEntryRow(index)}
                disabled={newEntries.length === 1}
                aria-label="Remove row"
              >
                <Trash2 width={18} height={18} />
              </button>
            </div>
          ))}
          <Button
            variant="text"
            endIcon={Plus}
            onClick={addNewEntryRow}
            className={style.addEntryRowButton}
            data-testid={`${name}-add-row`}
          >
            Add another row
          </Button>
          <div className={style.modalFooterActions}>
            <Button variant="outline" onClick={closeAddModal}>
              Cancel
            </Button>
            <Button
              onClick={handleAddEntries}
              data-testid={`${name}-confirm-add`}
            >
              {filledEntries.length > 1
                ? `Add ${filledEntries.length} investigations`
                : "Add"}
            </Button>
          </div>
        </div>
      </Dialog>
      <div
        className={style.investigationTableWrapper}
        data-testid={`${name}-table`}
      >
        <DataTable
          columns={columns}
          data={fields}
          getRowId={(row) => row.id}
          rowActions={rowActions}
          emptyTitle={`No ${sectionLabel.toLowerCase()} added`}
          emptyText="Import results from the patient file or add one manually."
          noScroll
          rowsPerPage={1000}
        />
      </div>
    </>
  );
};
